<template>
  <article>
    <notification />
    <div class="container-medium">
      <section class="split signup-cta">
        <div>
          <h4 v-t>Share your sign up link</h4>
          <div class="feature-item">
            <img src="~@/assets/images/signup-link-benefit-01.svg">
            <p v-t>Embed the link in your website or send it through email or text.</p>
          </div>
          <div class="feature-item">
            <img src="~@/assets/images/signup-link-benefit-02.svg">
            <p>
              <span v-t>
                Clients that sign up with this link are automatically associated with
                you. Their fee will be
              </span>
              <span>&nbsp;{{ account.defaultFee | formatPercentage }}.</span>
            </p>
          </div>
        </div>
        <img src="~@/assets/images/signup-link-illo.png" class="small-screen-hide illo">
      </section>

      <section class="alt">
        <div class="split advisor-link">
          <div class="split-text">
            <h5 v-t>Your sign up link</h5>
            <a :href="advisorLink" target="_blank" rel="nofollow noreferrer">{{ advisorLink }}</a>
          </div>
          <button
            id="copylink-button" v-clipboard:copy="advisorLink" v-clipboard:success="copySuccess" v-clipboard:error="copyError"
            class="silver split-action subtle icon before" type="button"
          >
            <span v-if="!copied">
              <svgicon name="clipboard" :alt="$t('copy to clipboard')" />
              <span v-t>Copy</span>
            </span>
            <span v-if="copied" v-t>Copied!</span>
          </button>
        </div>
        <div class="split advisor-fee">
          <div class="split-text">
            <span v-t>Planning Fee: </span>
            <strong>&nbsp;{{ account.defaultFee | formatPercentage }}</strong>
          </div>
          <button v-t class="link" @click="showEditFee">Edit</button>
        </div>
      </section>
      <section class="center large-screen-hide">
        <img src="~@/assets/images/signup-link-illo.png" class="illo">
      </section>
    </div>

    <modal title="Edit Fee" :displayed="displayEditFee" @close="closeEditFee">
      <button slot="title-action" v-t type="button" form="profileForm" @click.prevent="submitFee">Update</button>
      <vue-form
        slot="body" ref="profileForm" class="modal-body"
        :state="feeState" name="feeForm" enctype="multipart/form-data" @submit.prevent="submitFee"
      >
        <p v-t>
          New clients that sign up with your link will be billed at the new fee.
          Existing clients will keep being billed at their current rate.
        </p>
        <validate>
          <label for="planningfee">
            <span v-t>
              Planning Fee (Max
            </span>
            <span>&nbsp;{{ MAX_FEE }}</span>
            <span>%)</span>
          </label>
          <div class="fee-container" :class="{ error: inputError.planningfee.$invalid }">
            <input
              id="planningfee"
              v-model.number="planningFee" type="number" step="0.01"
              :min="MIN_FEE" :max="MAX_FEE" name="planningfee"
              :class="{ error: inputError.planningfee.$invalid }" required
              @blur="updateError('planningfee')"
            >
            <span class="fee-percentage" :class="{ neg: inputError.planningfee.$invalid }">&percnt;</span>
          </div>
          <div v-if="inputError.planningfee.$error" class="error-message">
            <div v-if="inputError.planningfee.$error.required">Required</div>
            <div v-if="inputError.planningfee.$error.min || inputError.planningfee.$error.max" v-t>Fee must be 0% to 1%</div>
          </div>
        </validate>
      </vue-form>
    </modal>
  </article>
</template>

<script>
import { modal, notification } from '@/components/layout';
import { mapGetters, mapActions } from '@/lib/vue';
import filters from '@/lib/utils/filters';
import getBrandConfig from '@/lib/utils/get_brand_config';
import { maxFee, minFee } from '@/lib/utils/constants';
import { translate as $t } from '@wealthbar/i18n';
import errorStore from '@/store/error';
import '@/lib/icons/clipboard';

const _ = { errorStore };

const brandConfig = getBrandConfig();

export default {
  _,
  filters,
  components: { modal, notification },
  head() {
    const title = this.$t('Sign Up Link');
    return {
      title: `${title} | ${brandConfig.fullName}`,
    };
  },

  data() {
    return {
      MIN_FEE: minFee,
      MAX_FEE: maxFee,
      feeState: {},
      inputError: {
        planningfee: {},
      },
      planningFee: null,
      copied: false,
      displayEditFee: false,
    };
  },

  computed: {
    ...mapGetters(['account', 'advisorLink']),
  },

  async created() {
    try {
      await this.fetchAccount();
      this.planningFee = this.account.defaultFee;
    } catch (exception) {
      _.errorStore.addError();
    }
  },

  methods: {
    ...mapActions(['fetchAccount', 'updateDefaultFee', 'showNotification']),

    copySuccess() {
      this.copied = true;
      window.setTimeout(() => { this.copied = false; }, 3000);
    },

    copyError() {
      this.showNotification({ icon: '/images/warning.png', message: $t('link failed to copy to clipboard') });
    },

    showEditFee() {
      this.displayEditFee = true;
      this.planningFee = this.account.defaultFee;
    },

    closeEditFee() {
      this.displayEditFee = false;
    },

    updateError(fieldName) {
      this.inputError[fieldName] = Object.assign({}, this.feeState[fieldName]);
      this.inputError[fieldName].$error = Object.assign({}, this.feeState[fieldName].$error);
    },

    async submitFee() {
      if (this.feeState.$invalid) {
        this.updateError('planningfee');
      } else {
        const account = Object.assign(this.account, {
          defaultFee: this.planningFee,
        });
        try {
          await this.updateDefaultFee(account);
          this.closeEditFee();
        } catch (exception) {
          _.errorStore.addError();
        }
      }
    },
  },
};
</script>
<style lang="scss" scoped>


//Remove once Peak Type system is in place

h4, .h4 {
  font-weight: bold;
  font-size: 1.125rem;
  line-height: 1.5rem;
  padding: 0;
  margin: 1.5rem 0 1rem;

  @media #{$screen-width-medium} {
    font-size: 1.25rem;
    line-height: 1.625rem;
    margin: 1.625rem 0 1rem;
  }
}

h5, .h5 {
  font-weight: bold;
  font-size: 1rem;
  line-height: 1.25rem;
  padding: 0;
  margin: 1.25rem 0 1rem;

  @media #{$screen-width-medium} {
    font-size: 1.125rem;
    line-height: 1.5rem;
    margin: 1.5rem 0 1rem;
  }
}

p { margin-bottom: 1rem; }

// -- end peak type -- //

.signup-cta {
  margin: 1rem 0;

  @media #{$screen-width-medium} {
    margin: 3rem 0;
  }
}

.feature-item {
  display: flex;
  margin-bottom: 1rem;

  img {
    width: 2rem;
    height: 2rem;
    margin-right: 1rem;
    color: $primary-500;
    @media #{$screen-width-medium} {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
}

.illo {
  max-width: 212px;
  height: auto;
  margin: auto;
}

.alt {
  display: flex;
  flex-direction: column;
  background: $primary-50;
  padding: 0 2rem;
  margin-bottom: 2rem;
  border-radius: 0.25rem;

  .advisor-link {
    padding: 0 0 1rem;
    border-bottom: 1px solid $pearl-300;
    align-items: flex-start;
    overflow-x: hidden;
    @media #{$screen-width-medium} { align-items: flex-end; }
    a { word-break: break-all; }
    button { margin: 1rem 0 0; }
  }

  .advisor-fee {
    padding: 1rem 0;
    flex-direction: row;
    justify-content: space-between;
  }
}

button svg { vertical-align: top; }
#copylink-button { width: 5.5rem; }

.modal-body {
  padding: 1rem;
  button { margin-top: 1rem; }
}

.fee-container {
  display: flex;
  align-items: center;
  padding: 0;

  .fee-percentage {
    margin-left: 0.25rem;
  }

  input {
    width: 4rem;
    margin: 0;
  }
}

</style>
